import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Chip,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';
import { canUserEditField } from '../utils/fieldVisibility';
import { validateForm as validateFormFields, groupErrorsByField } from '../utils/fieldValidation';
import HtmlContentField from '../components/HtmlContentField';
import PhoneInput from '../components/PhoneInput';
import NotFound from '../components/NotFound';
import axios from 'axios';

interface Field {
  id: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  editable?: boolean; // Whether this field can be edited
  options?: { value: string; label: string }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    custom?: {
      enabled: boolean;
      rules: Array<{
        id: string;
        type: 'compare_dates' | 'compare_numbers' | 'min_length' | 'max_length' | 'custom_pattern' | 'dependent_value';
        compareField?: string;
        compareOperator?: 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal' | 'equal' | 'not_equal';
        value?: string | number;
        message: string;
        dependentValue?: string;
      }>;
    };
  };
  pricing?: {
    enabled: boolean;
    options?: Record<string, number>;
  };
  visibility?: {
    level: 'public' | 'admin-only' | 'hidden';
    publicCanView?: boolean;
  };
  styling?: {
    textAlign?: 'left' | 'center' | 'right';
    fontSize?: string;
    fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
    color?: string;
    backgroundColor?: string;
    padding?: string;
    marginTop?: string;
    marginBottom?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  };
  htmlContent?: {
    content: string;
    enableVariables: boolean;
    systemVariables?: string[];
    fieldVariables?: string[];
  };
}

interface Submission {
  id: string;
  uniqueId: string;
  editCode: string;
  formId: string;
  form: {
    id: string;
    title: string;
    description?: string;
    allowEditing?: boolean; // Whether form allows editing
    fields: Field[];
  };
  data: Record<string, any>;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const EditSubmission: React.FC = () => {
  const { uniqueId, editCode } = useParams<{ uniqueId: string; editCode: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const loadSubmission = async () => {
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/submissions/edit/${uniqueId}/${editCode}`
        );

        setSubmission(response.data);

        // Check if form allows editing
        if (response.data.form.allowEditing === false) {
          setError('Editing is disabled for this form. Please contact the form administrator if you need to make changes.');
          return;
        }
        
        // Normalize form data to use field.id consistently
        const normalizedData: Record<string, any> = {};
        const fields = response.data.form.fields;
        
        // Map all field data to field.id keys for consistency
        fields.forEach((field: Field) => {
          const submissionData = response.data.data || {};
          const value = submissionData[field.id] || 
                       submissionData[field.name] || 
                       submissionData[`field_${field.id}`] || 
                       (field.type === 'checkbox' ? [] : '');
          normalizedData[field.id] = value;
        });
        
        setFormData(normalizedData);
        
        // Debug: Log the normalization process
        console.log('📋 Data normalization:', {
          originalData: response.data.data,
          normalizedData: normalizedData,
          fields: fields.map((f: Field) => ({ id: f.id, name: f.name, type: f.type }))
        });

      } catch (error: any) {
        console.error('Error loading submission:', error);
        if (error.response?.status === 404) {
          setNotFound(true);
        } else {
          setError('Failed to load submission. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSubmission();
  }, [uniqueId, editCode]);

  const reloadSubmission = async () => {
    try {
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/edit/${uniqueId}/${editCode}`
      );

      setSubmission(response.data);
      
      // Normalize form data to use field.id consistently  
      const normalizedData: Record<string, any> = {};
      const fields = response.data.form.fields;
      
      // Map all field data to field.id keys for consistency
      fields.forEach((field: Field) => {
        const submissionData = response.data.data || {};
        const value = submissionData[field.id] || 
                     submissionData[field.name] || 
                     submissionData[`field_${field.id}`] || 
                     (field.type === 'checkbox' ? [] : '');
        normalizedData[field.id] = value;
      });
      
      setFormData(normalizedData);

    } catch (error: any) {
      console.error('Error reloading submission:', error);
      setError('Failed to reload submission data.');
    }
  };

  const validateFormData = (): boolean => {
    if (!submission) return false;

    // Get editable fields for validation
    const editableFields = submission.form.fields.filter(field => 
      !['heading', 'paragraph', 'divider', 'html'].includes(field.type) &&
      canUserEditField(field as any, null)
    );
    
    // Convert formData (keyed by field.id) to validation format (keyed by field.name)
    // Include all fields for dependent validation, not just editable ones
    const formValuesForValidation: Record<string, any> = {};
    submission.form.fields.forEach(field => {
      formValuesForValidation[field.name] = formData[field.id];
    });
    
    // Use the comprehensive validation function
    const validationErrors = validateFormFields(editableFields, formValuesForValidation);
    
    // Group errors by field name for display
    const groupedErrors = groupErrorsByField(validationErrors);
    
    // Convert to the format expected by the component
    const errorMessages: { [key: string]: string } = {};
    Object.keys(groupedErrors).forEach(fieldName => {
      const fieldErrors = groupedErrors[fieldName];
      if (fieldErrors.length > 0) {
        // Join multiple error messages with newlines
        errorMessages[fieldName] = fieldErrors.map(err => err.message).join('\n');
      }
    });

    setValidationErrors(errorMessages);
    return validationErrors.length === 0;
  };

  const handleFieldChange = (field: Field, value: any) => {
    setFormData(prev => ({
      ...prev,
      // Use the field ID as the primary key for consistency
      [field.id]: value
    }));

    // Clear validation error when user starts editing
    if (validationErrors[field.name]) {
      setValidationErrors(prev => ({
        ...prev,
        [field.name]: ''
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Run comprehensive validation (includes custom rules)
      if (!validateFormData()) {
        setError('Please fix the validation errors before saving.');
        return;
      }

      // Additional check for required fields (in case validation missed anything)
      const requiredFields = submission?.form.fields.filter(field => field.required) || [];
      const missingFields = requiredFields.filter(field => {
        const fieldValue = formData[field.id];
        
        // For radio buttons, check if array has values or if string value exists
        if (field.type === 'radio') {
          return !fieldValue || 
                 (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                 (typeof fieldValue === 'string' && fieldValue.trim() === '');
        }
        
        // For checkboxes, check array length
        if (field.type === 'checkbox') {
          return !fieldValue || 
                 (Array.isArray(fieldValue) && fieldValue.length === 0);
        }
        
        // For other fields, check normal values
        return !fieldValue || 
               (Array.isArray(fieldValue) && fieldValue.length === 0);
      });

      if (missingFields.length > 0) {
        setError(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
        return;
      }

      console.log('🔄 Submitting edit:', { uniqueId, editCode, formData });

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/submissions/edit/${uniqueId}/${editCode}`,
        { data: formData }
      );

      console.log('✅ Edit submitted successfully');
      showSnackbar('Submission updated successfully!', 'success');
      
      // Reload to show updated data
      await reloadSubmission();

    } catch (error: any) {
      console.error('Error saving submission:', error);
      setError(error.response?.data?.message || 'Failed to save submission. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: Field) => {
    // Skip display fields completely for editing - they don't have data values
    if (['heading', 'paragraph', 'divider', 'html'].includes(field.type)) {
      switch (field.type) {
        case 'heading':
          return (
            <Typography 
              key={field.id}
              variant={field.styling?.headingLevel || 'h2'}
              sx={{
                textAlign: field.styling?.textAlign || 'left',
                fontSize: field.styling?.fontSize,
                fontWeight: field.styling?.fontWeight || 'normal',
                color: field.styling?.color,
                backgroundColor: field.styling?.backgroundColor,
                padding: field.styling?.padding,
                marginTop: field.styling?.marginTop,
                marginBottom: field.styling?.marginBottom,
              }}
            >
              {field.label || 'Heading Text'}
            </Typography>
          );

        case 'paragraph':
          return (
            <Typography 
              key={field.id}
              variant="body1"
              sx={{
                textAlign: field.styling?.textAlign || 'left',
                fontSize: field.styling?.fontSize,
                fontWeight: field.styling?.fontWeight || 'normal',
                color: field.styling?.color,
                backgroundColor: field.styling?.backgroundColor,
                padding: field.styling?.padding,
                marginTop: field.styling?.marginTop,
                marginBottom: field.styling?.marginBottom,
              }}
            >
              {field.label || 'Paragraph text content goes here...'}
            </Typography>
          );

        case 'divider':
          return (
            <Divider 
              key={field.id}
              sx={{
                backgroundColor: field.styling?.color,
                marginTop: field.styling?.marginTop || '16px',
                marginBottom: field.styling?.marginBottom || '16px',
              }}
            />
          );

        case 'html':
          return (
            <HtmlContentField
              key={field.id}
              field={field}
              formData={submission?.form}
              submissionData={submission}
              isPreview={false}
            />
          );

        default:
          return null;
      }
    }

    // Use consistent field mapping - always use field.id for consistency
    const value = formData[field.id] || 
                  (field.type === 'checkbox' ? [] : '');
    const isRequired = field.required;
    const isEditable = canUserEditField(field as any, null); // Use enhanced field visibility logic

    // If field is not editable, show as read-only
    if (!isEditable) {
      const readOnlyReason = field.pricing?.enabled 
        ? "Pricing field - locked to maintain payment integrity"
        : "Read-only";
      
      return (
        <Box key={field.id} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {field.label}
            {isRequired && <span style={{ color: 'red' }}> *</span>}
            <Chip 
              label={readOnlyReason} 
              size="small" 
              color="default" 
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              p: 1.5, 
              border: '1px solid', 
              borderColor: 'grey.300',
              borderRadius: 1,
              backgroundColor: 'grey.50',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {Array.isArray(value) ? value.join(', ') : value || 'No value'}
          </Typography>
        </Box>
      );
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        const hasError = !!validationErrors[field.name];
        const errorMessage = validationErrors[field.name];
        
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label}
            type={field.type}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            required={isRequired}
            margin="normal"
            variant="outlined"
            error={hasError}
            helperText={hasError ? errorMessage : ''}
          />
        );

      case 'tel':
        const telHasError = !!validationErrors[field.name];
        const telErrorMessage = validationErrors[field.name];
        
        return (
          <PhoneInput
            key={field.id}
            label={field.label}
            value={value}
            onChange={(number, fullNumber) => handleFieldChange(field, fullNumber)}
            required={isRequired}
            margin="normal"
            fullWidth
            error={telHasError}
            helperText={telHasError ? telErrorMessage : ''}
          />
        );

      case 'textarea':
        const textareaHasError = !!validationErrors[field.name];
        const textareaErrorMessage = validationErrors[field.name];
        
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            required={isRequired}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
            error={textareaHasError}
            helperText={textareaHasError ? textareaErrorMessage : ''}
          />
        );

      case 'date':
        const dateHasError = !!validationErrors[field.name];
        const dateErrorMessage = validationErrors[field.name];
        
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            required={isRequired}
            margin="normal"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            error={dateHasError}
            helperText={dateHasError ? dateErrorMessage : ''}
          />
        );

      case 'select':
        const selectHasError = !!validationErrors[field.name];
        const selectErrorMessage = validationErrors[field.name];
        
        return (
          <FormControl key={field.id} fullWidth margin="normal" required={isRequired} error={selectHasError}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              label={field.label}
            >
              {field.options?.map((option) => {
                // Handle both string options and object options
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {selectHasError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.75, mx: 1.75 }}>
                {selectErrorMessage}
              </Typography>
            )}
          </FormControl>
        );

      case 'radio':
        const radioHasError = !!validationErrors[field.name];
        const radioErrorMessage = validationErrors[field.name];
        
        return (
          <FormControl key={field.id} margin="normal" required={isRequired} error={radioHasError}>
            <Typography variant="subtitle1" gutterBottom>
              {field.label} {isRequired && '*'}
            </Typography>
            <RadioGroup
              value={Array.isArray(value) ? value[0] || '' : value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
            >
              {field.options?.map((option) => {
                // Handle both string options and object options
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                return (
                  <FormControlLabel
                    key={optionValue}
                    value={optionValue}
                    control={<Radio />}
                    label={optionLabel}
                  />
                );
              })}
            </RadioGroup>
            {radioHasError && (
              <Typography variant="caption" color="error">
                {radioErrorMessage}
              </Typography>
            )}
          </FormControl>
        );

      case 'checkbox':
        const checkboxHasError = !!validationErrors[field.name];
        const checkboxErrorMessage = validationErrors[field.name];
        
        return (
          <Box key={field.id} margin="normal">
            <Typography variant="subtitle1" gutterBottom color={checkboxHasError ? 'error' : 'inherit'}>
              {field.label} {isRequired && '*'}
            </Typography>
            {field.options?.map((option) => {
              // Handle both string options and object options
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              
              return (
                <FormControlLabel
                  key={optionValue}
                  control={
                    <Checkbox
                      checked={Array.isArray(value) ? value.includes(optionValue) : false}
                      onChange={(e) => {
                        const currentValues = Array.isArray(value) ? value : [];
                        const newValues = e.target.checked
                          ? [...currentValues, optionValue]
                          : currentValues.filter(v => v !== optionValue);
                        handleFieldChange(field, newValues);
                      }}
                    />
                  }
                  label={optionLabel}
                />
              );
            })}
            {checkboxHasError && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {checkboxErrorMessage}
              </Typography>
            )}
          </Box>
        );

      default:
        // For unknown field types that aren't handled above, don't render anything
        // This prevents display fields from being treated as input fields
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (notFound) {
    return (
      <NotFound
        title="Submission Not Found"
        message="The submission you're trying to edit was not found or the edit code is invalid. Please check your unique ID and edit code."
        showHomeButton={true}
        customAction={{
          label: "Try Again",
          onClick: () => window.location.reload()
        }}
      />
    );
  }

  if (error && !submission) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            startIcon={<ViewIcon />}
          >
            Go to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Edit Submission
          </Typography>
          
          {submission && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {submission.form.title}
                </Typography>
                {submission.form.description && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {submission.form.description}
                  </Typography>
                )}
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    label={`ID: ${submission.uniqueId}`}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`Status: ${submission.status}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`Last Updated: ${new Date(submission.updatedAt).toLocaleString()}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form Fields */}
        {submission && (
          <Box>
            {submission.form.fields
              .filter((field) => field.type !== 'html') // Hide HTML content fields from edit view
              .map((field) => (
              <Box key={field.id} mb={2}>
                {renderField(field)}
                {field.visibility?.level && field.visibility.level !== 'public' && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Field visibility: {field.visibility.level.replace('-', ' ')}
                  </Typography>
                )}
              </Box>
            ))}

            {/* Save Button */}
            <Box mt={4} display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                size="large"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                size="large"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* Instructions */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Editing Instructions:</strong>
          </Typography>
          <Typography variant="body2">
            • Only certain fields may be editable based on form settings<br/>
            • Required fields must be filled before saving<br/>
            • Changes are saved immediately when you click "Save Changes"<br/>
            • You can bookmark this page to edit your submission later
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default EditSubmission;
